import React__default, { useEffect } from 'react';
import { Avatar, Image, Button } from '@deal/components';
import { useApplicationContext, Application } from '../../../containers/ApplicationContext/index.js';
import classnames from 'classnames';
import MessageBubble from '../../MessageBubble/index.js';
import MessageBubbleText from '../../MessageBubbleText/index.js';
import { ConsumerRichCardEvents } from '../../../containers/RichCardEventEmitter/ConsumerEventData/index.js';
import { useRichCardEventEmitter } from '../../../containers/RichCardEventEmitter/index.js';
import { SimpleCuratedOrderType, OrderState } from '../../../generated/types.js';
import { simpleExpertCuratedOrderRichCard } from '../../../services/richCardFactory.js';
import HeroWithImage from '../components/HeroWithImage/index.js';
import numeral from 'numeral';
import { SimpleCuratedOrderMessageComponent } from './SimpleCuratedOrderMessage.generated.js';
import styles from './styles.css.js';

var TipSelector = function TipSelector(_a) {
  var _b;

  var order = _a.order;
  var isOrderPaid = order.state == OrderState.CONSUMER_PAYMENT_COMPLETED;
  var tipAmountPaid = isOrderPaid && order.tip ? numeral(order.tip.amount).value() : 0;
  var amountOptions = order.tipMethod.__typename === 'TipMethodFixedAmount' ? order.tipMethod.amountOptions : [];
  var isCustomTip = isOrderPaid && order.tip && amountOptions.find(function (option) {
    return numeral(option.amount).value() === tipAmountPaid;
  }) === undefined;
  return React__default.createElement("div", {
    className: styles.tipSelector
  }, amountOptions.map(function (option, idx) {
    var _a;

    var optionAmount = numeral(option.amount);
    var optionClasses = classnames(styles.tipOption, (_a = {}, _a[styles.checked] = optionAmount.value() === tipAmountPaid, _a));
    return React__default.createElement("div", {
      key: idx,
      className: optionClasses
    }, optionAmount.format('$0'));
  }), React__default.createElement("div", {
    className: classnames(styles.tipOption, (_b = {}, _b[styles.customTip] = isCustomTip, _b))
  }, isCustomTip ? numeral(tipAmountPaid).format('$0.00') : 'Custom'));
};

var GiftCardSelector = function GiftCardSelector(_a) {
  var _b;

  var order = _a.order,
      isConsumerApp = _a.isConsumerApp,
      richCardEventEmitter = _a.richCardEventEmitter;
  var isOrderPaid = order.state == OrderState.CONSUMER_PAYMENT_COMPLETED;
  var amountPaid = isOrderPaid && order.tip ? numeral(order.totalCost.amount).value() : 0;
  var amountOptions = [{
    amount: 200,
    currency: 'USD'
  }, {
    amount: 500,
    currency: 'USD'
  }, {
    amount: 1000,
    currency: 'USD'
  }];
  order.tipMethod.__typename === 'TipMethodFixedAmount' ? order.tipMethod.amountOptions : [];
  var isCustomAmount = isOrderPaid && order.totalCost && amountOptions.find(function (option) {
    return numeral(option.amount).value() === amountPaid;
  }) === undefined;
  return React__default.createElement("div", {
    className: styles.tipSelector
  }, amountOptions.map(function (option, idx) {
    var _a;

    var optionAmount = numeral(option.amount);
    var optionClasses = classnames(styles.tipOption, (_a = {}, _a[styles.checked] = optionAmount.value() === amountPaid, _a));
    return React__default.createElement("div", {
      key: idx,
      className: optionClasses,
      onClick: function onClick() {
        if (isConsumerApp) {
          richCardEventEmitter === null || richCardEventEmitter === void 0 ? void 0 : richCardEventEmitter.emitEvent({
            type: ConsumerRichCardEvents.GIFT_CARD_ORDER,
            orderId: order.id,
            amount: option.amount
          });
        }
      }
    }, optionAmount.format('$0,000'));
  }), React__default.createElement("div", {
    className: classnames(styles.tipOption, (_b = {}, _b[styles.customTip] = isCustomAmount, _b)),
    onClick: function onClick() {
      if (isConsumerApp) {
        richCardEventEmitter === null || richCardEventEmitter === void 0 ? void 0 : richCardEventEmitter.emitEvent({
          type: ConsumerRichCardEvents.GIFT_CARD_ORDER,
          orderId: order.id
        });
      }
    }
  }, isCustomAmount ? numeral(amountPaid).format('$0.00') : 'Custom'));
};

var SimpleCuratedOrderMessage = function SimpleCuratedOrderMessage(_a) {
  var message = _a.message,
      myself = _a.myself,
      onLoaded = _a.onLoaded;
  var richCardEventEmitter = useRichCardEventEmitter();
  var richCard = simpleExpertCuratedOrderRichCard(message);
  var orderId = richCard.simpleCuratedOrderId;
  var application = useApplicationContext().application;
  useEffect(function () {
    onLoaded();
  }, []);
  return React__default.createElement(SimpleCuratedOrderMessageComponent, {
    variables: {
      id: orderId
    }
  }, function (_a) {
    var data = _a.data,
        loading = _a.loading;

    var _b, _c;

    if (loading) {
      return React__default.createElement(MessageBubbleText, {
        message: message,
        myself: myself
      }, "Loading ...");
    } else if ((data === null || data === void 0 ? void 0 : data.simpleCuratedOrder.type) === SimpleCuratedOrderType.EXPERT_CONSULTATION_FEE) {
      var curatedByExpert = data.simpleCuratedOrder.curator;
      var order = data.simpleCuratedOrder.order;
      var isOrderPaid = data.simpleCuratedOrder.order.state == OrderState.CONSUMER_PAYMENT_COMPLETED;
      var tipAmountPaid = isOrderPaid && order.tip ? numeral(order.tip.amount).format('$0') : '0';
      var category = data.simpleCuratedOrder.lead && data.simpleCuratedOrder.lead.category;
      var banner = category && category.contentAssets.bannerImages && category.contentAssets.bannerImages[0];
      var expertName = curatedByExpert.displayName;
      return React__default.createElement(MessageBubble, {
        message: message
      }, React__default.createElement(HeroWithImage, {
        imageUrl: banner === null || banner === void 0 ? void 0 : banner.imageUrl,
        heroLabel: React__default.createElement(React__default.Fragment, null, React__default.createElement(Avatar, {
          size: 60,
          name: expertName,
          src: (_c = (_b = curatedByExpert.expertProfile) === null || _b === void 0 ? void 0 : _b.profilePhoto) === null || _c === void 0 ? void 0 : _c.url
        }), React__default.createElement("div", {
          className: styles.expertName
        }, expertName), React__default.createElement("div", {
          className: styles.tipExplanation
        }, "100% of your tip goes to ", curatedByExpert.shortName))
      }), isOrderPaid ? React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
        className: styles.caption
      }, React__default.createElement("div", null, React__default.createElement("strong", null, "Thank you for your ", tipAmountPaid, " tip!")), React__default.createElement("div", null, "We're glad you enjoyed working with ", curatedByExpert.shortName)), React__default.createElement("div", {
        className: styles.tipSelectorDisabled
      }, React__default.createElement("div", {
        className: styles.selectTipAmount
      }, "Tip amount"), React__default.createElement(TipSelector, {
        order: order
      }))) : React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
        className: styles.caption
      }, "Thank your expert for their time and expertise by leaving an optional tip!"), React__default.createElement("div", {
        className: styles.tipSelectorEnabled
      }, React__default.createElement("div", {
        className: styles.selectTipAmount
      }, "Select tip amount"), application === Application.CONSUMER ? React__default.createElement("div", {
        onClick: function onClick() {
          return richCardEventEmitter === null || richCardEventEmitter === void 0 ? void 0 : richCardEventEmitter.emitEvent({
            type: ConsumerRichCardEvents.VIEW_ORDER_CHECKOUT,
            orderId: data.simpleCuratedOrder.order.id
          });
        }
      }, React__default.createElement(TipSelector, {
        order: order
      })) : React__default.createElement(TipSelector, {
        order: order
      }))));
    } else if ((data === null || data === void 0 ? void 0 : data.simpleCuratedOrder.type) === SimpleCuratedOrderType.GIFT_CARD) {
      var order = data.simpleCuratedOrder.order;
      var isOrderPaid = data.simpleCuratedOrder.order.state == OrderState.CONSUMER_PAYMENT_COMPLETED;
      return React__default.createElement(MessageBubble, {
        message: message
      }, React__default.createElement("div", {
        className: styles.cardContainer
      }, React__default.createElement("div", {
        className: styles.card
      }, React__default.createElement(Image, {
        src: data.simpleCuratedOrder.giftCardImageUrl,
        width: 156,
        className: styles.sellableIconImage
      }))), isOrderPaid ? React__default.createElement("div", {
        className: styles.caption
      }, React__default.createElement("div", null, React__default.createElement("strong", null, "Thank you for purchasing the gift card"))) : React__default.createElement(React__default.Fragment, null, React__default.createElement("div", null, "\xA0"), React__default.createElement("div", {
        className: styles.tipSelectorEnabled
      }, React__default.createElement("div", {
        className: styles.selectTipAmount
      }, "Select gift card amount"), React__default.createElement(GiftCardSelector, {
        order: order,
        richCardEventEmitter: richCardEventEmitter,
        isConsumerApp: application === Application.CONSUMER
      }))));
    } else if ((data === null || data === void 0 ? void 0 : data.simpleCuratedOrder.type) === SimpleCuratedOrderType.CURATED_INSIDERS_MEMBERSHIP) {
      var isOrderPaid = data.simpleCuratedOrder.order.state == OrderState.CONSUMER_PAYMENT_COMPLETED;
      return React__default.createElement(MessageBubble, {
        message: message
      }, React__default.createElement("div", {
        className: styles.cardContainer
      }, React__default.createElement("div", {
        className: styles.card
      }, React__default.createElement(Image, {
        src: data.simpleCuratedOrder.curatedInsidersImageUrl,
        width: 156,
        className: styles.sellableIconImage
      }))), isOrderPaid ? React__default.createElement("div", {
        className: styles.caption
      }, React__default.createElement("div", null, React__default.createElement("strong", null, "Thank you for purchasing Curated Insiders"))) : React__default.createElement("div", {
        className: styles.checkoutButtonRow
      }, React__default.createElement("div", {
        className: styles.checkoutButtonBox
      }, React__default.createElement(Button, {
        className: styles.checkoutButton,
        disabled: application !== Application.CONSUMER,
        onClick: function onClick() {
          if (application === Application.CONSUMER) {
            richCardEventEmitter === null || richCardEventEmitter === void 0 ? void 0 : richCardEventEmitter.emitEvent({
              type: ConsumerRichCardEvents.VIEW_ORDER_CHECKOUT,
              orderId: data.simpleCuratedOrder.order.id
            });
          }
        }
      }, "Checkout"))));
    } else {
      return React__default.createElement(MessageBubbleText, {
        message: message,
        myself: myself
      }, "Error loading messages!");
    }
  });
};

SimpleCuratedOrderMessage.handlesMessage = function (message) {
  return !!simpleExpertCuratedOrderRichCard(message);
};

export default SimpleCuratedOrderMessage;
//# sourceMappingURL=index.js.map
